import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Trophy, Flame, Award } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { capture } from "@/lib/analytics";

const TRIGGER_DEFS = {
  three_challenges: {
    storage_key: "trigger_3_challenges_shown",
    icon: Trophy,
    badge: "🟡 Progress Update",
    heading: "You're building momentum.",
    body: "Unlock advanced challenge paths and continue growing — every belt unlocks faster.",
    cta: "Continue Growing",
    accent: "#F59E0B",
    bg: "linear-gradient(135deg, rgba(251,191,36,0.10) 0%, rgba(245,158,11,0.05) 100%)",
  },
  seven_day_streak: {
    storage_key: "trigger_7_streak_shown",
    icon: Flame,
    badge: "🔥 Streak Milestone",
    heading: "You're building real momentum.",
    body: "Unlock deeper challenge tracks and accelerate your progress — Mastery users belt 2× faster.",
    cta: "Advance Faster",
    accent: "#FF5C2A",
    bg: "linear-gradient(135deg, rgba(255,92,42,0.10) 0%, rgba(245,158,11,0.05) 100%)",
  },
  belt_unlock: {
    storage_key: "trigger_belt_unlock_shown",
    icon: Award,
    badge: "🥋 Belt Achievement",
    heading: "You've completed more training than 74% of users.",
    body: "Unlock specialized learning paths and continue advancing — your belt path opens up.",
    cta: "Continue Your Journey",
    accent: "#7C3AED",
    bg: "linear-gradient(135deg, rgba(124,58,237,0.10) 0%, rgba(59,130,246,0.05) 100%)",
  },
};

/** Hook: pop the appropriate trigger once per user per type, max once per session.
 *
 * Caller passes a current "milestone state" object — when a transition crosses
 * a threshold, the trigger fires. Subscribers + lower-priority triggers are
 * skipped if a higher-priority one already showed in this session.
 */
export function useConversionTrigger() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);

  // Subscribers never see conversion prompts.
  const isSubscriber = !!user?.has_subscription || user?.tier === "mastery" || user?.tier === "pro";

  const maybeFire = (kind) => {
    if (isSubscriber) return;
    if (!user || !TRIGGER_DEFS[kind]) return;
    if (active) return;
    try {
      const k = TRIGGER_DEFS[kind].storage_key + "_" + (user.user_id || "anon");
      if (sessionStorage.getItem(k) === "1") return;
      if (localStorage.getItem(k) === "1") return; // already shown this user, ever
      sessionStorage.setItem(k, "1");
      localStorage.setItem(k, "1");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[conversion-trigger] storage access blocked:", e?.message);
      // Private-mode fallback: just show the trigger this session (no persistence).
    }
    capture("trigger_shown", { trigger_type: kind });
    setActive(kind);
  };

  return {
    activeTrigger: active,
    dismiss: () => {
      if (active) capture("trigger_dismissed", { trigger_type: active });
      setActive(null);
    },
    fireThreeChallenges: () => maybeFire("three_challenges"),
    fireSevenDayStreak: () => maybeFire("seven_day_streak"),
    fireBeltUnlock: () => maybeFire("belt_unlock"),
  };
}

/** Modal rendered above the page when a trigger fires.
 *  Single-source-of-truth — render once at the top of a page that uses triggers.
 */
export default function ConversionTriggerModal({ trigger, onDismiss }) {
  const navigate = useNavigate();
  const def = trigger ? TRIGGER_DEFS[trigger] : null;

  // Lock body scroll while open
  useEffect(() => {
    if (!def) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, [def]);

  const onConvert = () => {
    capture("trigger_converted", { trigger_type: trigger });
    navigate("/pricing?from=trigger&type=" + trigger);
  };

  return (
    <AnimatePresence>
      {def && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ background: "rgba(15,20,25,0.55)", backdropFilter: "blur(8px)" }}
          onClick={onDismiss}
          data-testid={`trigger-modal-${trigger}`}
        >
          <motion.div
            initial={{ y: 32, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.97, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="brut-lg bg-white max-w-md w-full p-7 sm:p-8 relative"
            style={{ background: def.bg }}
          >
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors"
              data-testid={`trigger-modal-${trigger}-dismiss`}
            >
              <X size={16} className="text-[var(--ink-2)]" />
            </button>

            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: def.accent }}
            >
              <def.icon size={26} color="#fff" strokeWidth={2.4} />
            </div>

            <div className="mt-4 text-xs uppercase tracking-wider font-semibold" style={{ color: def.accent }}>
              {def.badge}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl mt-1 leading-tight">{def.heading}</h3>
            <p className="text-sm sm:text-base text-[var(--ink-2)] mt-3 leading-relaxed">{def.body}</p>

            <div className="mt-6 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={onConvert}
                className="btn-duo w-full justify-center"
                data-testid={`trigger-modal-${trigger}-cta`}
              >
                {def.cta} <ArrowRight size={14} />
              </button>
              <button
                type="button"
                onClick={onDismiss}
                className="text-sm text-[var(--ink-3)] hover:text-[var(--ink-2)] py-2"
                data-testid={`trigger-modal-${trigger}-keep`}
              >
                Keep training for now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
